// The one client-side splitter for a run into laps/segments.
//
// The speed/airspeed Plotter has always been the de-facto correct
// implementation: it splits the whole run on `segment_ts` gate dividers,
// including the lead-in and run-out pieces, and asks `Gate_NG10` which
// segments are actual traverses. Every other lap-parsing feature that
// re-derived that logic got the edge cases wrong until it ended up mimicking
// the Plotter. This module is that logic lifted out so there is one copy.
//
// Background, the competing representations (`lap_markers`,
// `TraverseLap_NG10`) and the footguns: .claude-notes/lap-segmentation-canonical.md

// Index of the first sample at or after time `t` (binary search). `arr` is a
// monotonic time axis, normally the gridded `TimeRel_tG00`. Returns -1 when
// empty, and the last index when every sample is before `t`.
export function findGridIdxAtOrAfter(arr: number[], t: number): number {
  const n = arr.length;
  if (n === 0) return -1;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid] < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export interface RunSegment {
  // Segment bounds in run-relative seconds (the plot's x axis).
  x1: number;
  x2: number;
  // 1-based divider index: the lead-in is 1, the segment after divider i is
  // i + 2, the run-out is segmentTs.length + 1. This is the number the Plotter
  // draws faded at the top of each band, and the index into
  // `exclude_candidates_ts` (candidate `rawLap - 1`). It is not the position
  // in the returned array — a run with no lead-in starts at rawLap 2.
  rawLap: number;
  // Mean `Gate_NG10` over the segment, rounded: 0 means "not a traverse"
  // (lead-in, turnaround, run-out), N > 0 means traverse N. `null` when there
  // is no gate/time channel to read, or no sample lands inside the segment.
  // Computed regardless of exclusion — callers decide whether to show it.
  traverseNo: number | null;
  // The `exclude_candidates_ts` value for this segment, or null when the
  // candidate list does not reach this far (a run-out past the last gate).
  excludeCandidate: number | null;
  // True when this segment's candidate appears in the run's `exclude_ts`.
  excluded: boolean;
}

export interface BuildRunSegmentsOptions {
  // Full run extent — the min/max of the plotted x values, so the lead-in and
  // run-out cover the parts of the run outside the gate dividers.
  minX: number;
  maxX: number;
  // `run.exclude_candidates_ts` — segment midpoints, one per divider,
  // positionally aligned with `rawLap`.
  excludeCandidates?: number[];
  // `run.exclude_ts` — the subset of candidates the user has dropped.
  excludeTs?: number[];
}

// Mean of `gateChannel` over the samples whose time falls in [x1, x2], rounded.
// Null when there is nothing to read.
function meanGateOver(
  timeChannel: number[] | undefined,
  gateChannel: number[] | undefined,
  x1: number,
  x2: number,
): number | null {
  if (!gateChannel || !timeChannel || timeChannel.length === 0) return null;
  const lo = findGridIdxAtOrAfter(timeChannel, x1);
  if (lo < 0) return null;
  let sum = 0;
  let count = 0;
  for (let i = lo; i < timeChannel.length && timeChannel[i] <= x2; i++) {
    sum += gateChannel[i];
    count++;
  }
  if (count === 0) return null;
  return Math.round(sum / count);
}

// Split [minX, maxX] on the `segment_ts` dividers into ordered segments.
//
// The split covers the whole run: a lead-in before the first divider (only
// when the run starts before it), one segment per consecutive divider pair,
// and a run-out after the last divider (only when the run ends after it).
// Returns [] when there are no dividers — there is nothing to split on, and
// callers should render the run unsegmented rather than as one big lap.
export function buildRunSegments(
  timeChannel: number[] | undefined,
  segmentTs: number[] | undefined,
  gateChannel: number[] | undefined,
  options: BuildRunSegmentsOptions,
): RunSegment[] {
  if (!segmentTs || segmentTs.length === 0) return [];
  const {
    minX, maxX, excludeCandidates, excludeTs,
  } = options;

  // `rawLap` is fixed to the divider index, not to the position in the output:
  // the lead-in is always 1, the segment after divider i is always i + 2, and
  // the run-out is always segmentTs.length + 1. That numbering is what keeps
  // `excludeCandidates[rawLap - 1]` aligned, because the server builds one
  // candidate per divider with the first measured from 0 (the lead-in). A run
  // whose first divider is at the very start therefore has no lap 1 at all.
  const bounds: { x1: number; x2: number; rawLap: number }[] = [];
  if (segmentTs[0] > minX) {
    bounds.push({ x1: minX, x2: segmentTs[0], rawLap: 1 });
  }
  for (let i = 0; i < segmentTs.length - 1; i++) {
    bounds.push({ x1: segmentTs[i], x2: segmentTs[i + 1], rawLap: i + 2 });
  }
  const lastTs = segmentTs[segmentTs.length - 1];
  if (maxX > lastTs) {
    bounds.push({ x1: lastTs, x2: maxX, rawLap: segmentTs.length + 1 });
  }

  return bounds.map((b) => {
    const { rawLap } = b;
    const candidate = excludeCandidates?.[rawLap - 1];
    const excludeCandidate = candidate === undefined ? null : candidate;
    return {
      x1: b.x1,
      x2: b.x2,
      rawLap,
      traverseNo: meanGateOver(timeChannel, gateChannel, b.x1, b.x2),
      excludeCandidate,
      excluded: excludeCandidate !== null && (excludeTs?.includes(excludeCandidate) ?? false),
    };
  });
}
